import { Injectable } from "@angular/core";
import { map, take } from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AppLinksService } from "../applinks/app-links.service";
import { BannerActionType, BannerInterface, CampaignInterface, GameInterface, OfferType } from "src/app/interfaces/lobby.interface";
import { CarouselProperties } from '../../interfaces/carousel-properties';
import { CashierContentService } from "../cashier/cashier-content.service";
import { ConfigService } from "../config/config.service";
import { ControlHelperService } from '../control/control-helper.service';
import { ControlService } from '../control/control.service';
import { DefaultCarouselProperties } from "src/app/interfaces/carousel-properties";
import { EventsService } from "../event/event.service";
import { GameTileService } from "../game-tile/game-tile.service";
import { Navigator } from "../routing/navigator";
import { RoutingService } from "../routing/routing.service";
import { StructureHelperService } from "../structure/structure-helper.service";
import { UserSessionService } from "../user-session/user-session.service";
import { DepositButtonService } from "../cashier/deposit-button.service";

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class BannerService {

  private readonly DEFAULT_CAROUSEL_PROPERTIES = new DefaultCarouselProperties()
  private bannerImageBaseUrl: string;
  private carouselProperties: CarouselProperties;
  private userLoggedIn: boolean = false;

  constructor(
    private appLinks: AppLinksService,
    private cashierContentService: CashierContentService,
    private config: ConfigService,
    private controlService: ControlService,
    private events: EventsService,
    private gameTileService: GameTileService,
    private helper: ControlHelperService,
    private navigator: Navigator,
    private routing: RoutingService,
    private structureHelper: StructureHelperService,
    private userSession: UserSessionService,
    private depositButtonService: DepositButtonService,
  ) {

    this.setBannerImageBaseUrl();
    this.verifyAuth();
    this.setCarouselProperties();

  }

  //============= Set the base URL for banner images ==============
  setBannerImageBaseUrl(): void {
    this.bannerImageBaseUrl = `${this.config.getBaseUrl()}/assets/notifications`;
  }

  // ============Set Carousel Properties==============
  setCarouselProperties(): void {
    const carouselFeatureResponse = this.config.getConfig().FEATURE_CAROUSEL_SETTINGS;
    try {
      this.carouselProperties = JSON.parse(carouselFeatureResponse) as CarouselProperties
    }
    catch (e) {
      this.carouselProperties = this.DEFAULT_CAROUSEL_PROPERTIES;
      // console.warn(
      //   '⚠️ JSON parse error for FEATURE_CAROUSEL_SETTINGS:',
      //   carouselFeatureResponse,
      //   e
      // );

    }

  }

  //===============Getter Carousel ===================    
  getCarouselProperties = (): CarouselProperties => this.carouselProperties;

  //===========Subscribe to user login status==============
  private verifyAuth(): void {
    this.userSession.sessionToken$
      .pipe(
        map(token => !!token),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.userLoggedIn = this.userSession.isAuthenticated();
      });
  }

  // ============Fetch all banners==============
  fetchAllBanners() {

    //Now from Notification when deposit is approved.

    console.warn(
      '%c🚫 [Deprecated] fetchAllBanners() is no longer in use. Kept for future reference in case balance updates are needed on-demand.',
      'color: #a187be; font-weight: bold; background-color: #f3e8ff; padding: 4px; border-radius: 4px;'
    );
    return false;
  }

  // ============Update the image URLs for the banners==============
  // This method takes an array of BannerInterface objects and returns a new array with updated image URLs
  updateImageUrls(items: BannerInterface[] = []): BannerInterface[] {

    return items.map(banner => ({
      ...banner,
      imageLarge: this.getImageUrl(banner?.imageLarge, 'larg.png'),
      imageMedium: this.getImageUrl(banner?.imageMedium, 'mid.png'),
      imageSmall: this.getImageUrl(banner?.imageSmall, 'sm.png'),
    }));
  }


  // ============Handle the selected campaign based on its action type==============
  handleSelectedCampaign(item: any): void {
    const actionType = this.getBannerActionType(item);

    switch (actionType) {
      case BannerActionType.ACQUISITION:
        this.handleAcquisitionOffer(item.campaignDefinitionInfo);
        break;
      case BannerActionType.DEPOSIT:
        this.handleDeposit(item?.campaignDefinitionInfo);
        break;
      case BannerActionType.SIGNUP:
        this.navigateToSignup(item);
        break;
      case BannerActionType.LOGIN:
        this.handleLoginAction();
        break;
      case BannerActionType.EXTERNAL_URL:
        this.openExternalUrl(item.url);
        break;
      case BannerActionType.GAME:
        this.launchGame(item.gameLaunchInfo);
        break;
      case BannerActionType.INTERNAL_NAV:
        this.handleInternalNavigation(item)
        break;
      case BannerActionType.CATEGORY:
        this.handleCategoryNavigation(item);
        break;
      case BannerActionType.ARTICLE:
        this.handleArticleNavigation(item);
        break;
      case BannerActionType.DIALOG:
        this.handleDialogNavigation(item);
        break;
      default:
        console.warn('Unhandled banner action type', item);
    }
  }

  //==================Handle the Category Navigation================
  private handleCategoryNavigation(item: any): void {

    this.structureHelper.findCategoryById(item?.category?.id).pipe(take(1)).subscribe(category => {
      this.controlService.selectCategory(category);
      this.helper.setActiveLobbyCategory(category);
    });

  }
  //==================Handle the Article Navigation================
  private handleArticleNavigation(item: any): void {
    const route = item?.url?.split('/').pop();
    this.helper.controlOpenWebview(route);
    this.controlService.setCategoryForWordPressPage();
    this.routing.toWebviewTab(true, route);
  }

  //==================Handle the Dialog Navigation================
  private handleDialogNavigation(item: any): void {
    const route = item?.url?.split('/').pop();
    this.helper.controlNavigateTo(route);
  }


  // ============Handle the internal navigation action==============
  private handleInternalNavigation(item: any): void {
    this.helper.onAlert(item);
    console.warn(`[BannerService: handleInternalNavigation]:  actionType "${item.actionType}" for item:`, item);
  }

  // ================Handle Login action================
  private handleLoginAction(): any {

    if (!this.userLoggedIn) return this.navigator.navigateTo("LoginComponent", { forceModal: true });
    return

  }

  // ============Get the action type of the banner==============
  private getBannerActionType(item: BannerInterface): BannerActionType {
    if (item.actionType === 'CAMPAIGN') {
      return (item.campaignDefinitionInfo?.banner_action || item.campaignDefinitionInfo?.campaign_type) as BannerActionType;
    }
    return item.actionType as BannerActionType;
  }

  // ============Get the image URL for the banner==============
  private getImageUrl(imageName: string | undefined, fallback: string): string {
    return `${this.bannerImageBaseUrl}/${imageName || fallback}`;
  }

  // ============Navigate to the signup page==============
  private navigateToSignup(item: BannerInterface): void {
    this.navigator.navigateTo(
      "SignupStepOneComponent",
      {},
      { signupCode: item.campaignDefinitionInfo?.campaign_code }
    );
  }

  // ============Launch the game based on the game data==============
  private launchGame(gameData: GameInterface): void {
    try {
      this.gameTileService.launchGame(gameData);
    } catch (error) {
      console.error('Failed to launch game:', error);
    }
  }

  // ============Open the external URL==============
  private openExternalUrl(url: string): void {
    if (url) {
      this.appLinks.openExternalPage(url);
    }
  }

  // ============Handle the acquisition offer based on its type==============
  private handleAcquisitionOffer(campaignInfo?): void {
    const offerType = campaignInfo?.offer_type;

    switch (offerType) {
      case OfferType.MFREESPIN:
        this.navigator.navigateTo("MyFreeSpinComponent");
        break;
      case OfferType.COUPON_CODE:
        this.navigator.navigateTo("RedeemBonusCodeComponent", {}, { bonusCode: campaignInfo?.campaign_code });
        break;
      default:
        console.warn('Unhandled acquisition offer type:', offerType);
    }
  }

  // ============Handle the deposit action based on the campaign ID==============
  private handleDeposit(categoryData?: CampaignInterface): void {
    this.depositButtonService.onPromoDeposit(categoryData, this.userLoggedIn)
  }
}